import React from 'react';

export type CustomerFacingSlaReportAppendix = {
  key: string;
  kind: 'list' | 'schedule';
  title: string;
  summary: string;
  columns: string[];
  rows: string[][];
};

export type CustomerFacingSlaReportSection = {
  title: string;
  summary: string;
  includedItems: string[];
};

export type CustomerFacingSlaReport = {
  title: string;
  subtitle?: string;
  overview: string;
  sections: CustomerFacingSlaReportSection[];
  appendices: CustomerFacingSlaReportAppendix[];
  savedAt?: string | null;
};

const formatSavedDate = (value?: string | null) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return parsed.toLocaleDateString();
};

export const normalizeCustomerFacingSlaReport = (input: any): CustomerFacingSlaReport => {
  const safe = input && typeof input === 'object' ? input : {};
  const sections = Array.isArray(safe.sections)
    ? safe.sections.map((section: any) => ({
        title: String(section?.title || '').trim() || 'Section',
        summary: String(section?.summary || '').trim(),
        includedItems: Array.isArray(section?.includedItems)
          ? section.includedItems.map((item: any) => String(item || '').trim()).filter(Boolean)
          : []
      }))
    : [];
  const appendices = Array.isArray(safe.appendices)
    ? safe.appendices.map((appendix: any) => ({
        key: String(appendix?.key || appendix?.title || '').trim() || 'appendix',
        kind: appendix?.kind === 'schedule' ? 'schedule' : 'list',
        title: String(appendix?.title || '').trim() || 'Appendix',
        summary: String(appendix?.summary || '').trim(),
        columns: Array.isArray(appendix?.columns)
          ? appendix.columns.map((column: any) => String(column || '').trim()).filter(Boolean)
          : [],
        rows: Array.isArray(appendix?.rows)
          ? appendix.rows.map((row: any) =>
              Array.isArray(row) ? row.map((cell: any) => String(cell ?? '').trim()) : []
            )
          : []
      }))
    : [];
  return {
    title: String(safe.title || '').trim() || 'Customer-facing SLA agreement',
    subtitle: String(safe.subtitle || '').trim(),
    overview: String(safe.overview || '').trim(),
    sections,
    appendices,
    savedAt: String(safe.savedAt || '').trim() || null
  };
};

export function CustomerFacingSlaReportView(props: { report: CustomerFacingSlaReport; showSavedAt?: boolean }) {
  const { report, showSavedAt = true } = props;
  const savedDateLabel = formatSavedDate(report.savedAt);

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <section
        style={{
          border: '1px solid #d7c6a3',
          borderRadius: '0.9rem',
          background: '#fffdf8',
          padding: '1rem',
          display: 'grid',
          gap: '0.85rem'
        }}
      >
        {report.overview ? (
          <div
            style={{
              fontSize: '1.02rem',
              lineHeight: 1.7,
              color: '#143f43'
            }}
          >
            {report.overview}
          </div>
        ) : null}
        {showSavedAt && savedDateLabel ? (
          <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
            Saved {savedDateLabel}
          </div>
        ) : null}
      </section>

      {report.sections.map((section, sectionIndex) => (
        <section
          key={`${section.title}-${sectionIndex}`}
          style={{
            border: '1px solid #d7c6a3',
            borderRadius: '0.9rem',
            background: '#ffffff',
            padding: '1rem',
            display: 'grid',
            gap: '0.75rem'
          }}
        >
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#143f43' }}>{section.title}</div>
          {section.summary ? <div style={{ color: '#475569', lineHeight: 1.6 }}>{section.summary}</div> : null}
          {section.includedItems.length > 0 ? (
            <div style={{ display: 'grid', gap: '0.45rem' }}>
              {section.includedItems.map(item => (
                <div key={`${section.title}-${item}`} style={{ color: '#143f43', lineHeight: 1.5 }}>
                  - {item}
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ))}

      {report.appendices.length > 0 ? (
        <section
          style={{
            border: '1px solid #d7c6a3',
            borderRadius: '0.9rem',
            background: '#ffffff',
            padding: '1rem',
            display: 'grid',
            gap: '0.9rem'
          }}
        >
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#143f43' }}>Appendices</div>
          {report.appendices.map(appendix => (
            <div
              key={appendix.key}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '0.85rem',
                padding: '0.9rem',
                display: 'grid',
                gap: '0.7rem'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ fontWeight: 800, color: '#143f43' }}>{appendix.title}</div>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>
                  {appendix.kind === 'schedule' ? 'Schedule Appendix' : 'List Appendix'}
                </div>
              </div>
              {appendix.summary ? <div style={{ color: '#475569', lineHeight: 1.55 }}>{appendix.summary}</div> : null}
              {appendix.columns.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {appendix.columns.map(column => (
                          <th
                            key={column}
                            style={{
                              textAlign: 'left',
                              padding: '0.7rem 0.75rem',
                              borderBottom: '1px solid #d7c6a3',
                              color: '#143f43',
                              fontSize: '0.8rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em'
                            }}
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {appendix.rows.map((row, rowIndex) => (
                        <tr key={`${appendix.key}-${rowIndex}`}>
                          {appendix.columns.map((column, columnIndex) => (
                            <td
                              key={`${appendix.key}-${column}-${rowIndex}`}
                              style={{
                                padding: '0.7rem 0.75rem',
                                borderBottom: '1px solid #f1f5f9',
                                color: '#1f2937',
                                verticalAlign: 'top'
                              }}
                            >
                              {row[columnIndex] || '--'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
